import { Badge, Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { humanizeConceptLabel } from "@/features/personas/lib/persona-format";

import { formatScriptCreatedAt, scriptMetaLine } from "../../../lib/persona-script-meta.js";

import type { HubChromeScriptRow } from "./hub-chrome.types.js";

/** Briefs are written for a single creator, as on the single-set brief card. */
const BRIEF_CREATOR_COUNT = 1;

/** Concept and meta lines under a hub script or brief title, matching the single-set workspace. */
export function OpportunityHubScriptMeta({
  row,
  showCreatedAt = true,
  className,
}: {
  row: HubChromeScriptRow;
  /** Off where a Generated at column already shows the time. */
  showCreatedAt?: boolean;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const createdAtLabel = showCreatedAt ? formatScriptCreatedAt(row.createdAt, i18n.language) : "";
  const createdAt = createdAtLabel ? (
    <span className="inline-flex items-center gap-1">
      <Icon name="clock" size="xs" color="muted" aria-hidden />
      {createdAtLabel}
    </span>
  ) : null;
  const versions =
    row.versionCount > 1 ? (
      <span
        role="img"
        aria-label={t("personaOpportunities.scripts.history.versionCount", {
          defaultValue_one: "{{count}} version",
          defaultValue_other: "{{count}} versions",
          count: row.versionCount,
        })}
        className="inline-flex items-center gap-1"
      >
        <Icon name="history" size="xs" color="muted" aria-hidden />
        {row.versionCount}
      </span>
    ) : null;

  if (row.kind === "brief") {
    if (row.hookPackage == null) return null;
    return (
      <div className={cn("flex flex-col gap-0.5", className)}>
        <p className="text-xs italic text-muted-foreground">
          {t("personaOpportunities.scripts.briefs.kindMeta", {
            defaultValue_one: "Creator brief · {{hooks}} hook",
            defaultValue_other: "Creator brief · {{hooks}} hooks",
            count: row.hookPackage,
            hooks: row.hookPackage,
          })}
        </p>
        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] tabular-nums text-muted-foreground">
          <span>
            {t("personaOpportunities.scripts.briefs.meta", {
              defaultValue_one: "{{hooks}} hook · {{creators}} creator · {{duration}}",
              defaultValue_other: "{{hooks}} hooks · {{creators}} creator · {{duration}}",
              count: row.hookPackage,
              hooks: row.hookPackage,
              duration: row.duration ?? "",
              creators: BRIEF_CREATOR_COUNT,
            })}
          </span>
          {createdAt}
          {versions}
        </p>
      </div>
    );
  }

  const normalizedConcept = row.concept?.trim()
    ? humanizeConceptLabel(row.concept.trim())
    : null;
  const normalizedTitle = row.title?.trim() ?? "";
  const concept =
    normalizedConcept &&
    normalizedConcept.localeCompare(normalizedTitle, undefined, { sensitivity: "accent" }) !== 0
      ? normalizedConcept
      : null;
  const hookCount = row.hookCount ?? 1;

  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      {concept ? <p className="text-xs italic text-muted-foreground">{concept}</p> : null}
      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] tabular-nums text-muted-foreground">
        <span>
          {scriptMetaLine(
            { slotIndex: row.slotIndex, sceneCount: row.sceneCount ?? 0, duration: row.duration },
            t,
            row.variantMeta,
          )}
        </span>
        {hookCount > 1 ? (
          <Badge variant="slate">
            {t("personaOpportunities.scripts.hookCountBadge", {
              defaultValue_one: "{{count}} hook",
              defaultValue_other: "{{count}} hooks",
              count: hookCount,
            })}
          </Badge>
        ) : null}
        {createdAt}
        {versions}
      </p>
    </div>
  );
}
